import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, MapPin, Clock, ArrowRight, X } from 'lucide-react';
export function ContactSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    budget: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`New Lead: ${formData.name} — ${formData.company || 'No Company'}`);
    const body = encodeURIComponent(`New project inquiry from PCS website:\n\n` + `Name: ${formData.name}\n` + `Email: ${formData.email}\n` + `Company: ${formData.company || 'N/A'}\n` + `Budget Range: ${formData.budget || 'Not specified'}\n\n` + `Project Details:\n${formData.message}`);
    window.open(`mailto:pinnaclecreativesolutionco@gmail.com?subject=${subject}&body=${body}`, '_blank');
    setSubmitted(true);
  };
  const handleClose = () => {
    setModalOpen(false);
    if (submitted) {
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          budget: '',
          message: ''
        });
      }, 300);
    }
  };
  return <>
      <section id="contact" className="py-24 md:py-32 bg-[#0A0A0A] relative" data-id="element-30">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" data-id="element-31" />

        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-32">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} className="text-center mb-16" data-id="element-33">
            <span className="text-blue-500 font-medium tracking-widest text-sm uppercase mb-4 block" data-id="element-34">
              Get In Touch
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-35">
              Contact Us
            </h2>
            <p className="text-xl text-neutral-400 max-w-2xl mx-auto" data-id="element-36">
              Ready to elevate your digital presence? Tell us about your project
              and we'll get back to you within 24 hours.
            </p>
          </motion.div>

          {/* Info Cards */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-37">
            <div className="flex items-start gap-4 bg-[#111111] border border-white/10 rounded-sm p-6" data-id="element-38">
              <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-39">
                <Mail size={20} className="text-blue-500" data-id="element-40" />
              </div>
              <div className="min-w-0" data-id="element-41">
                <h3 className="font-display font-semibold text-white mb-1 text-sm" data-id="element-42">
                  Email
                </h3>
                <p className="text-neutral-400 text-xs sm:text-sm break-all" data-id="element-43">
                  pinnaclecreativesolutionco@gmail.com
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#111111] border border-white/10 rounded-sm p-6" data-id="element-44">
              <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center shrink-0" data-id="element-45">
                <MapPin size={22} className="text-blue-500" data-id="element-46" />
              </div>
              <div data-id="element-47">
                <h3 className="font-display font-semibold text-white mb-1" data-id="element-48">
                  Location
                </h3>
                <p className="text-neutral-400 text-sm" data-id="element-49">
                  Remote-first · Serving clients globally
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-[#111111] border border-white/10 rounded-sm p-6" data-id="element-50">
              <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center shrink-0" data-id="element-51">
                <Clock size={22} className="text-blue-500" data-id="element-52" />
              </div>
              <div data-id="element-53">
                <h3 className="font-display font-semibold text-white mb-1" data-id="element-54">
                  Response Time
                </h3>
                <p className="text-neutral-400 text-sm" data-id="element-55">
                  Within 24 hours on business days
                </p>
              </div>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-center" data-id="element-56">
            <button onClick={() => setModalOpen(true)} className="group inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 text-white px-12 py-5 rounded-sm font-medium text-lg transition-all shadow-[0_0_30px_rgba(59,130,246,0.15)]" data-id="element-57">
              Start Your Project
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-58" />
            </button>
            <p className="text-neutral-500 text-sm mt-4" data-id="element-59">
              Free consultation · No commitment required
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Modal */}
      <AnimatePresence data-id="element-60">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={handleClose} data-id="element-61">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="bg-[#111111] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-62">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0" data-id="element-63">
                <div data-id="element-64">
                  <h2 className="font-display text-xl font-bold text-white" data-id="element-65">
                    Start Your Project
                  </h2>
                  <p className="text-neutral-500 text-sm mt-1" data-id="element-66">
                    Tell us about your vision
                  </p>
                </div>
                <button onClick={handleClose} className="text-neutral-400 hover:text-white transition-colors" data-id="element-67">
                  <X size={20} data-id="element-68" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto" data-id="element-69">
                {submitted ? <div className="py-12 text-center" data-id="element-70">
                    <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-71">
                      <Send size={28} className="text-blue-500" data-id="element-72" />
                    </div>
                    <h3 className="font-display text-2xl font-bold text-white mb-3" data-id="element-73">
                      Message Sent
                    </h3>
                    <p className="text-neutral-400 mb-8" data-id="element-74">
                      Thank you for reaching out. Our team will review your
                      inquiry and respond within 24 hours.
                    </p>
                    <button onClick={handleClose} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-3 rounded-sm font-medium transition-colors text-sm" data-id="element-75">
                      Close
                    </button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-76">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-77">
                      <div data-id="element-78">
                        <label htmlFor="modal-name" className="block text-sm font-medium text-neutral-300 mb-2" data-id="element-79">
                          Full Name *
                        </label>
                        <input type="text" id="modal-name" name="name" required value={formData.name} onChange={handleChange} placeholder="John Smith" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-blue-500/50 transition-colors" data-id="element-80" />
                      </div>
                      <div data-id="element-81">
                        <label htmlFor="modal-email" className="block text-sm font-medium text-neutral-300 mb-2" data-id="element-82">
                          Work Email *
                        </label>
                        <input type="email" id="modal-email" name="email" required value={formData.email} onChange={handleChange} placeholder="john@company.com" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-blue-500/50 transition-colors" data-id="element-83" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-84">
                      <div data-id="element-85">
                        <label htmlFor="modal-phone" className="block text-sm font-medium text-neutral-300 mb-2" data-id="element-86">
                          Contact Number
                        </label>
                        <input type="tel" id="modal-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-blue-500/50 transition-colors" data-id="element-87" />
                      </div>
                      <div data-id="element-88">
                        <label htmlFor="modal-company" className="block text-sm font-medium text-neutral-300 mb-2" data-id="element-89">
                          Company
                        </label>
                        <input type="text" id="modal-company" name="company" value={formData.company} onChange={handleChange} placeholder="Your Company" className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-blue-500/50 transition-colors" data-id="element-90" />
                      </div>
                      <div data-id="element-91">
                        <label htmlFor="modal-budget" className="block text-sm font-medium text-neutral-300 mb-2" data-id="element-92">
                          Budget Range
                        </label>
                        <select id="modal-budget" name="budget" value={formData.budget} onChange={handleChange} className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500/50 transition-colors appearance-none" data-id="element-93">
                          <option value="" className="text-neutral-600" data-id="element-94">
                            Select a range
                          </option>
                          <option value="1500-3500" data-id="element-95">$1,500 – $3,500</option>
                          <option value="3500-8000" data-id="element-96">$3,500 – $8,000</option>
                          <option value="8000+" data-id="element-97">$8,000+</option>
                          <option value="not-sure" data-id="element-98">Not sure yet</option>
                        </select>
                      </div>
                    </div>

                    <div data-id="element-99">
                      <label htmlFor="modal-message" className="block text-sm font-medium text-neutral-300 mb-2" data-id="element-100">
                        Tell Us About Your Project *
                      </label>
                      <textarea id="modal-message" name="message" required rows={4} value={formData.message} onChange={handleChange} placeholder="Describe your goals, current challenges, and what success looks like for you..." className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-blue-500/50 transition-colors resize-none" data-id="element-101" />
                    </div>

                    <div className="flex items-center gap-4 pt-2" data-id="element-102">
                      <button type="button" onClick={() => {
                  const subject = encodeURIComponent(`Inquiry from ${formData.company || formData.name}`);
                  const body = encodeURIComponent(`Name: ${formData.name}\r\nEmail: ${formData.email}\r\nPhone: ${formData.phone || 'N/A'}\r\nCompany: ${formData.company || 'N/A'}\r\nBudget: ${formData.budget || 'Not specified'}\r\n\r\nProject Details:\r\n${formData.message}`);
                  window.location.href = `mailto:pinnaclecreativesolutionco@gmail.com?subject=${subject}&body=${body}`;
                  setSubmitted(true);
                }} className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white w-full px-10 py-4 rounded-sm font-semibold transition-all" data-id="element-103">
                        Send Message
                        <Send size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-104" />
                      </button>
                      <button type="button" onClick={handleClose} className="text-neutral-500 hover:text-white text-sm transition-colors" data-id="element-105">
                        Cancel
                      </button>
                    </div>
                  </form>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}